/**
 * Gift Selection Hook - Intelligent Logic Processor
 *
 * CORE PRINCIPLE:
 * - Hook xử lý TẤT CẢ logic về gifts một cách thông minh
 * - Session ID là KEY bắt buộc cho MỌI operation
 * - Hook tự động lấy data từ store và xử lý logic
 * - Consumer chỉ cần truyền sessionId, hook lo phần còn lại
 *
 * SESSION ID PATTERN:
 * - PDP: `gift-${productId}-pdp` - tạo khi load trang
 * - Cart: Lấy từ cart item object (như lấy rules)
 * - Khi chọn gift: lưu sessionId vào business object
 *
 * INTELLIGENT FEATURES:
 * - Auto-calculate eligibility from session data
 * - Auto-calculate remaining quantity per round
 * - Auto-validate gift selection logic
 * - Auto-suggest best gift based on rules
 * - Auto-track selection rounds
 */
import { useCallback, useEffect, useRef } from 'react';
import { type FreeGiftsData, type ProductInterface, type Rule } from '../../../types/front.graphql';
import { type SelectedGift } from '../types/gift-selection.types';
import { type GiftSelectionSession, type GiftSelectionEntityType } from '../stores/simple-gift-selection.store';
import { giftSelectionStoreAPI } from '../stores/simple-gift-selection-store.public-api';
import type { Observable } from '@legendapp/state';

/**
 * Parameters for initializing a gift selection session
 */
export interface InitializeSessionParams {
    sessionId?: string;
    entityId: string;
    entityType: GiftSelectionEntityType;
    availableGifts: FreeGiftsData[];
    productQuantity: number;
}

/**
 * Hook has no props - pure logic processor
 */
export interface UseGiftSelectionReturn {
    // Session lifecycle - require sessionId + data
    initializeSession: (params: InitializeSessionParams) => string;

    updateSession: (sessionId: string, availableGifts: FreeGiftsData[], productQuantity: number) => void;

    clearSession: (sessionId: string) => void;
    destroySession: (sessionId: string) => void;

    // Gift selection - sessionId is key, hook handles logic
    selectGift: (sessionId: string, gift: SelectedGift, selectionRound: number) => void;
    removeGift: (sessionId: string, giftId: string) => void;

    // Auto-suggest best gift based on session data
    autoSelectBestGift: (sessionId: string, selectionRound: number) => SelectedGift | null;

    // Query session state
    getSelectedGifts: (sessionId: string) => SelectedGift[];
    getProductQuantity: (sessionId: string) => number;
    getAvailableGifts: (sessionId: string) => FreeGiftsData[];
    getActiveSessions: () => GiftSelectionSession[];

    // Active session management
    setActiveSession: (sessionId: string, entityId: string, entityType: GiftSelectionEntityType) => void;
    getActiveSession: (entityId: string, entityType: GiftSelectionEntityType) => GiftSelectionSession | undefined;
    getActiveSessionId: (entityId: string, entityType: GiftSelectionEntityType) => string | undefined;
    clearActiveSession: (entityId: string, entityType: GiftSelectionEntityType) => void;

    // Computed values - hook calculates intelligently
    isComplete: (sessionId: string) => boolean;
    getRemainingQuantity: (sessionId: string, forRound: number) => number;
    getGiftsForRound: (sessionId: string, round: number) => SelectedGift[];

    // UI state
    getIsPopupOpen: (sessionId: string) => boolean;
    setIsPopupOpen: (sessionId: string, open: boolean) => void;
    checkAndShowPopup: (sessionId: string) => void;

    // Observable for reactive updates
    getStoreObservable: (sessionId: string) => Observable<GiftSelectionSession> | undefined;
}

// ============================================================================
// HELPER FUNCTIONS - Gift Logic Processing
// ============================================================================

/**
 * Calculate gift eligibility based on rule and quantity
 */
const calculateGiftEligibility = (rule: Rule, quantity: number) => {
    const buyX = rule.discount_step || 1;
    const getY = rule.discount_amount || 1;

    const eligibleSets = Math.floor(quantity / buyX);
    const maxFreeGifts = eligibleSets * getY;
    const quantityUsedByRule = eligibleSets * buyX;

    return {
        canGetGifts: eligibleSets > 0,
        maxFreeGifts,
        requiredQuantity: buyX,
        buyQuantity: quantity,
        quantityUsedByRule,
    };
};

/**
 * Suggest best gift - rule with HIGHEST buy requirement
 */
const suggestBestGift = (
    availableGifts: FreeGiftsData[],
    quantity: number,
    selectionRound: number,
    sessionId: string
): SelectedGift | null => {
    let bestGift: SelectedGift | null = null;
    let maxBuyRequirement = 0;

    availableGifts.forEach(giftData => {
        const rule = giftData.rule;
        const products = giftData.products || [];

        if (rule && products.length > 0) {
            const eligibility = calculateGiftEligibility(rule, quantity);

            if (eligibility.canGetGifts) {
                const buyRequirement = rule.discount_step || 1;

                if (buyRequirement > maxBuyRequirement) {
                    const bestProduct = products[0] as ProductInterface;
                    if (bestProduct?.id && rule?.rule_id) {
                        maxBuyRequirement = buyRequirement;
                        bestGift = {
                            giftSlots: eligibility.maxFreeGifts,
                            id: `${rule.rule_id}-${bestProduct.id}-${selectionRound}`,
                            ruleId: rule.rule_id,
                            productId: bestProduct.id,
                            selectionRound,
                            item: bestProduct,
                            giftData: giftData,
                            quantity: eligibility.maxFreeGifts,
                            quantityUsedByRule: eligibility.quantityUsedByRule,
                            selectedAt: Date.now(),
                            sessionId,
                        };
                    }
                }
            }
        }
    });

    return bestGift;
};

/**
 * Check if selection is complete for a session
 */
const isSelectionComplete = (selectedGifts: SelectedGift[], quantity: number): boolean => {
    if (selectedGifts.length === 0) return false;

    const selectedGift = selectedGifts[0];
    const rule = selectedGift.giftData?.rule;
    if (!rule) return false;

    const eligibility = calculateGiftEligibility(rule, quantity);
    return eligibility.canGetGifts;
};

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export const useGiftSelection = (): UseGiftSelectionReturn => {
    const debounceTimersRef = useRef<Record<string, NodeJS.Timeout>>({});

    // Cleanup on unmount
    useEffect(() => {
        const timers = debounceTimersRef.current;
        return () => {
            Object.values(timers).forEach(timer => clearTimeout(timer));
        };
    }, []);

    // ========================================================================
    // SESSION LIFECYCLE
    // ========================================================================

    const initializeSession = useCallback(
        ({ sessionId, entityId, entityType, availableGifts, productQuantity }: InitializeSessionParams) => {
            const currentSession = giftSelectionStoreAPI.state.getSessionById(sessionId ?? '');
            if (currentSession) {
                console.log('🎁 [useGiftSelection] Session already exists:', sessionId);
                return currentSession?.sessionId;
            }
            return giftSelectionStoreAPI.actions.createSession(
                entityId,
                entityType,
                availableGifts,
                3, // maxTurns
                productQuantity
            );
        },
        []
    );

    const updateSession = useCallback((sessionId: string, availableGifts: FreeGiftsData[], productQuantity: number) => {
        if (giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            console.log('🎁 [useGiftSelection] Updating session:', sessionId);
            giftSelectionStoreAPI.actions.updateSession(sessionId, {
                availableGifts,
                productQuantity,
            });
        }
    }, []);

    const clearSession = useCallback((sessionId: string) => {
        console.log('🎁 [useGiftSelection] Clearing session:', sessionId);
        if (giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            giftSelectionStoreAPI.actions.clearSelectedGifts(sessionId);
        }
    }, []);

    const destroySession = useCallback((sessionId: string) => {
        console.log('🎁 [useGiftSelection] Destroying session:', sessionId);
        if (giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            giftSelectionStoreAPI.actions.deleteSession(sessionId);
            if (debounceTimersRef.current[sessionId]) {
                clearTimeout(debounceTimersRef.current[sessionId]);
                delete debounceTimersRef.current[sessionId];
            }
        }
    }, []);

    // ========================================================================
    // GIFT SELECTION - INTELLIGENT LOGIC
    // ========================================================================

    const selectGift = useCallback((sessionId: string, gift: SelectedGift, selectionRound: number) => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            console.warn('🎁 [useGiftSelection] Session does not exist:', sessionId);
            return;
        }

        // Get session data
        const session = giftSelectionStoreAPI.state.getSessionById(sessionId);
        console.log('🎁 [useGiftSelection] selectGift called:', { sessionId, gift, selectionRound, session });
        if (!session) return;

        const productQuantity = session.productQuantity || 0;
        const currentGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId) as SelectedGift[];

        const giftsInCurrentRound = currentGifts.filter(g => g.selectionRound === selectionRound);
        // Only keep gifts from PREVIOUS rounds (< selectionRound).
        // Gifts from subsequent rounds (> selectionRound) must be cleared
        // because remaining quantity changes when current round selection changes.
        const giftsInPreviousRounds = currentGifts.filter(g => g.selectionRound < selectionRound);

        // Check if gift already selected - toggle behavior
        const existingGiftInRound = giftsInCurrentRound.find(g => g.id === gift.id);
        if (existingGiftInRound) {
            // subsequent rounds since remaining quantity changes
            giftSelectionStoreAPI.actions.setSelectedGifts(
                sessionId,
                currentGifts.filter(g => g.selectionRound <= selectionRound)
            );
            return;
        }

        // Calculate remaining quantity for this round
        let remainingQuantity = productQuantity;
        console.log('remainingQuantity before calculation:', remainingQuantity);
        const previousRoundsGifts = currentGifts
            .filter(g => g.selectionRound < selectionRound)
            .sort((a, b) => a.selectionRound - b.selectionRound);

        previousRoundsGifts.forEach(prevGift => {
            remainingQuantity -= prevGift.quantityUsedByRule || 0;
        });

        // Validate eligibility
        const rule = gift.giftData?.rule;
        const eligibility = rule ? calculateGiftEligibility(rule, remainingQuantity) : null;

        if (!eligibility || !eligibility.canGetGifts) {
            console.warn('🎁 [useGiftSelection] Gift not eligible:', gift.id);
            return;
        }

        // Create updated gift
        const updatedGift: SelectedGift = {
            ...gift,
            id: `${rule?.rule_id ?? gift.ruleId}-${gift.productId}-${selectionRound}`,
            selectionRound,
            giftSlots: eligibility.maxFreeGifts,
            quantity: eligibility.maxFreeGifts,
            quantityUsedByRule: eligibility.quantityUsedByRule,
            sessionId,
        };

        console.log('🎁 [useGiftSelection] Selecting gift:', updatedGift);

        // Replace gift in current round, clear subsequent rounds (remaining quantity changed)
        giftSelectionStoreAPI.actions.setSelectedGifts(sessionId, [...giftsInPreviousRounds, updatedGift]);
    }, []);

    const removeGift = useCallback((sessionId: string, giftId: string) => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            console.warn('🎁 [useGiftSelection] Session does not exist:', sessionId);
            return;
        }
        giftSelectionStoreAPI.actions.removeGift(sessionId, giftId);
    }, []);

    const autoSelectBestGift = useCallback((sessionId: string, selectionRound: number): SelectedGift | null => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            return null;
        }

        const session = giftSelectionStoreAPI.state.getSessionById(sessionId);
        if (!session) return null;

        const availableGifts = session.availableGifts || [];
        const currentGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId) as SelectedGift[];

        // Calculate remaining quantity
        let remainingQuantity = session.productQuantity || 0;
        const previousRoundsGifts = currentGifts
            .filter(g => g.selectionRound < selectionRound)
            .sort((a, b) => a.selectionRound - b.selectionRound);

        previousRoundsGifts.forEach(prevGift => {
            remainingQuantity -= prevGift.quantityUsedByRule || 0;
        });

        const bestGift = suggestBestGift(availableGifts, remainingQuantity, selectionRound, sessionId);

        if (bestGift) {
            // Only keep gifts from previous rounds (< selectionRound).
            // Clear subsequent rounds because remaining quantity changes with new selection.
            const giftsInPreviousRounds = currentGifts.filter(g => g.selectionRound < selectionRound);
            giftSelectionStoreAPI.actions.setSelectedGifts(sessionId, [...giftsInPreviousRounds, bestGift]);
        }

        return bestGift;
    }, []);

    // ========================================================================
    // QUERY SESSION STATE
    // ========================================================================

    const getSelectedGifts = useCallback((sessionId: string): SelectedGift[] => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            return [];
        }
        return giftSelectionStoreAPI.state.getSelectedGifts(sessionId) as SelectedGift[];
    }, []);

    const getProductQuantity = useCallback((sessionId: string): number => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            return 0;
        }
        return giftSelectionStoreAPI.state.getProductQuantity(sessionId);
    }, []);

    const getAvailableGifts = useCallback((sessionId: string): FreeGiftsData[] => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            return [];
        }
        return [...giftSelectionStoreAPI.state.getAvailableGifts(sessionId)];
    }, []);

    const getActiveSessions = useCallback((): GiftSelectionSession[] => {
        const sessionsRecord = giftSelectionStoreAPI.state.getAllSessions();
        return Object.values(sessionsRecord);
    }, []);

    // ========================================================================
    // ACTIVE SESSION MANAGEMENT
    // ========================================================================

    const setActiveSession = useCallback((sessionId: string, entityId: string, entityType: GiftSelectionEntityType) => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            console.warn('🎁 [useGiftSelection] Session does not exist:', sessionId);
            return;
        }
        console.log('🎁 [useGiftSelection] Setting active session:', { sessionId, entityId, entityType });
        giftSelectionStoreAPI.actions.setActiveSession(sessionId, entityId, entityType);
    }, []);

    const getActiveSession = useCallback(
        (entityId: string, entityType: GiftSelectionEntityType): GiftSelectionSession | undefined => {
            return giftSelectionStoreAPI.state.getActiveSession(entityId, entityType);
        },
        []
    );

    const getActiveSessionId = useCallback(
        (entityId: string, entityType: GiftSelectionEntityType): string | undefined => {
            return giftSelectionStoreAPI.state.getActiveSessionId(entityId, entityType);
        },
        []
    );

    const clearActiveSession = useCallback((entityId: string, entityType: GiftSelectionEntityType) => {
        console.log('🎁 [useGiftSelection] Clearing active session:', { entityId, entityType });
        giftSelectionStoreAPI.actions.clearActiveSession(entityId, entityType);
    }, []);

    // ========================================================================
    // COMPUTED VALUES - INTELLIGENT CALCULATIONS
    // ========================================================================

    const isComplete = useCallback((sessionId: string): boolean => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            return false;
        }

        const session = giftSelectionStoreAPI.state.getSessionById(sessionId);
        if (!session) return false;

        const selectedGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId) as SelectedGift[];
        return isSelectionComplete(selectedGifts, session.productQuantity || 0);
    }, []);

    const getRemainingQuantity = useCallback((sessionId: string, forRound: number): number => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            return 0;
        }
        return giftSelectionStoreAPI.computed.getRemainingQuantity(sessionId, forRound);
    }, []);

    const getGiftsForRound = useCallback(
        (sessionId: string, round: number): SelectedGift[] => {
            const allGifts = getSelectedGifts(sessionId);
            return allGifts.filter(g => g.selectionRound === round);
        },
        [getSelectedGifts]
    );

    // ========================================================================
    // UI STATE
    // ========================================================================

    const getIsPopupOpen = useCallback((sessionId: string): boolean => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            return false;
        }
        const session = giftSelectionStoreAPI.state.getSessionById(sessionId);
        return session?.isPopupOpen ?? false;
    }, []);

    const setIsPopupOpen = useCallback((sessionId: string, open: boolean) => {
        if (!giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
            console.warn('🎁 [useGiftSelection] Session does not exist:', sessionId);
            return;
        }
        giftSelectionStoreAPI.actions.setPopupOpen(sessionId, open);
    }, []);

    const checkAndShowPopup = useCallback(
        (sessionId: string) => {
            if (!isComplete(sessionId) && giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
                giftSelectionStoreAPI.actions.setPopupOpen(sessionId, true);
            }
        },
        [isComplete]
    );

    // ========================================================================
    // OBSERVABLE
    // ========================================================================

    const getStoreObservable = useCallback((sessionId: string) => {
        return giftSelectionStoreAPI.state.getSessionObservable(sessionId);
    }, []);

    // ========================================================================
    // RETURN API
    // ========================================================================

    return {
        // Session lifecycle
        initializeSession,
        updateSession,
        clearSession,
        destroySession,

        // Gift operations
        selectGift,
        removeGift,
        autoSelectBestGift,

        // Query state
        getSelectedGifts,
        getProductQuantity,
        getAvailableGifts,
        getActiveSessions,

        // Active session management
        setActiveSession,
        getActiveSession,
        getActiveSessionId,
        clearActiveSession,

        // Computed
        isComplete,
        getRemainingQuantity,
        getGiftsForRound,

        // UI state
        getIsPopupOpen,
        setIsPopupOpen,
        checkAndShowPopup,

        // Observable
        getStoreObservable,
    };
};
